import { Card } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  AccountRateCreateReqSchemaUI,
  IAccountRateCreateReqSchema,
  IAccountRateCreateReqSchemaUI,
  IAccountRateUpdateReqSchemaUI,
  IAccountRespSchema,
  IPriceUnderProductSchemaUI,
  IQuoteItemWithDisplayType,
  IQuoteOfferingRespSchema,
  IRateReqSchema,
  IRateResSchema,
  OfferingTypesEnum,
} from '@monetize/types/app';
import { IOrderedObj } from '@monetize/types/common';
import { ExperienceConfigurationKeys } from '@monetize/types/experience-config';
import {
  roundNumberToDecimal,
  sortByProductPosition,
} from '@monetize/utils/core';
import { useQueryClient } from '@tanstack/react-query';
import { FC, Fragment, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { handleApiErrorToast } from '../../../api/axios';
import { quoteOfferingRateServiceQueryKeys } from '../../../api/queryKeysService';
import {
  QuoteOfferingRateEditability,
  QuoteOfferingRateEditReq,
  useEditQuoteOfferingRate,
} from '../../../api/quoteOfferingRateService';
import { cacheOfferingRate, useExperienceConfiguration } from '../../../hooks';
import { getPricesFromProductObject } from '../../../routes/ProductCatalog/Offerings/components/Rate/RatePriceUtils';
import { logger } from '../../../services/logger';
import { getOrderedPricesUnderProduct } from '../../../utils/product';
import { MButton, MCard, MDivider, MFlex } from '../../Monetize';
import { ExpandCollapseButton } from '../../Monetize/ExpandCollapseButton';
import { PricesDrawerBodyEditProduct } from './PricesDrawerBodyEditProduct';
import {
  BulkPriceChange,
  PricesDrawerBulkEditPrices,
} from './PricesDrawerBulkEditPrices';
import { PricesDrawerPriceTierLocking } from './PricesDrawerPriceTierLocking';
import { PricesDrawerRateHeader } from './PricesDrawerRateHeader';
import { PricesDrawerScopedProducts } from './PricesDrawerScopedProducts';
import {
  applyRateEditToRate,
  prepareRateRequestData,
  validateBlockPricing,
} from './utils';

interface PricesDrawerBodyEditProps {
  account: IAccountRespSchema | Pick<IAccountRespSchema, 'id' | 'accountName'>;
  selectedProductsWithPrices: IPriceUnderProductSchemaUI[];
  productsWithPrices: IPriceUnderProductSchemaUI[];
  offeringRate: IRateResSchema;
  productsObj: IOrderedObj<IQuoteItemWithDisplayType>;
  isEditMode: boolean;
  offeringType: OfferingTypesEnum;
  setIsEditMode: (val: boolean) => void;
  rateEditMode: 'clone' | 'edit';
  setRateEditMode: (mode: 'clone' | 'edit') => void;
  editability?: QuoteOfferingRateEditability;
  isEditabilityLoading?: boolean;
  onRateSaved?: (rate: IRateResSchema) => Promise<void> | void;
  onRateEdited?: (rate: IRateResSchema) => Promise<void> | void;
  createOfferingRate: (
    data: IRateReqSchema | IAccountRateCreateReqSchema,
    offeringId?: string | undefined,
  ) => Promise<IRateResSchema | null>;
  setOfferingRate?: (val: IRateResSchema | null) => void;
  setOrderedPricesUnderProduct?: (val: IPriceUnderProductSchemaUI[]) => void;
  disabled?: boolean;
  onCancel: () => void;
  onSavingChange?: (isSaving: boolean) => void;
  eligibleProductIds: ReadonlySet<string>;
  quoteOffering: IQuoteOfferingRespSchema;
}

const PRODUCTS_COLLAPSE_THRESHOLD = 10;
const PRICES_COLLAPSE_THRESHOLD = 20; // Total prices count of all the products

export const PricesDrawerBodyEdit: FC<PricesDrawerBodyEditProps> = ({
  account,
  selectedProductsWithPrices,
  productsWithPrices,
  offeringRate,
  productsObj,
  isEditMode,
  offeringType,
  setIsEditMode,
  rateEditMode,
  setRateEditMode,
  editability,
  isEditabilityLoading,
  onRateSaved,
  onRateEdited,
  createOfferingRate,
  setOfferingRate,
  setOrderedPricesUnderProduct,
  onCancel,
  onSavingChange,
  disabled,
  eligibleProductIds,
  quoteOffering,
}: PricesDrawerBodyEditProps) => {
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [shouldExpandAllProducts, setShouldExpandAllProducts] = useState(false);
  const [isBulkPriceChangeActive, setIsBulkPriceChangeActive] = useState(false);
  const [isPriceTierLockingMode, setIsPriceTierLockingMode] = useState(false);
  const { accountSpecificRateAllowPercentageAdjustments } =
    useExperienceConfiguration(
      ExperienceConfigurationKeys.quoteAndContractSettings,
    );
  const editRate = useEditQuoteOfferingRate();
  const queryClient = useQueryClient();

  const defaultValues = prepareRateRequestData(
    account,
    productsWithPrices,
    offeringRate,
  );
  const isInPlaceEdit =
    isEditMode && rateEditMode === 'edit' && !!editability?.editable;
  const methods = useForm<
    IAccountRateCreateReqSchemaUI | IAccountRateUpdateReqSchemaUI
  >({
    mode: 'onChange',
    reValidateMode: 'onChange',
    resolver: zodResolver(AccountRateCreateReqSchemaUI) as any,
    defaultValues,
    values: defaultValues,
    resetOptions: {
      keepDirty: true,
      keepErrors: true,
    },
  });
  const {
    getValues,
    setValue,
    reset,
    handleSubmit,
    setError,
    formState: { isDirty },
    watch,
  } = methods;
  const watchProducts = watch('products');
  const sortedProductsWithPrices = sortByProductPosition(
    selectedProductsWithPrices.map((item) => ({
      ...item,
      position: productsObj.byId[item.id]?.position ?? 0,
      quantityDisplayType: productsObj.byId[item.id]?.quantityDisplayType,
    })),
  );

  useEffect(() => {
    if (!watchProducts) {
      return;
    }

    const productIds = Object.keys(watchProducts);
    const totalPricesCount = productIds.reduce(
      (sum, productId) => sum + (watchProducts[productId].prices?.length || 0),
      0,
    );

    const shouldExpand =
      productIds.length <= PRODUCTS_COLLAPSE_THRESHOLD ||
      totalPricesCount <= PRICES_COLLAPSE_THRESHOLD;

    setShouldExpandAllProducts(shouldExpand);
  }, [watchProducts]);

  function handleClone() {
    setRateEditMode('clone');
    setIsEditMode(true);
  }

  function handleEdit() {
    setRateEditMode('edit');
    setIsEditMode(true);
  }

  function handleCancelEdit() {
    setIsEditMode(false);
  }

  /**
   * User has created or edited a custom rate
   */
  async function onSubmit({
    products,
    ...rest
  }: IAccountRateCreateReqSchemaUI | IAccountRateUpdateReqSchemaUI) {
    // Validate block pricing
    if (validateBlockPricing({ products, ...rest }, setError)) {
      return;
    }

    // Validate percent of total configuration
    if (offeringType === OfferingTypesEnum.CUSTOM_PERCENT_OF_TOTAL) {
      const { minimumTotal, maximumTotal } = rest.percentOfTotalConfig ?? {};

      if (
        minimumTotal != null &&
        maximumTotal != null &&
        minimumTotal > maximumTotal
      ) {
        setError('percentOfTotalConfig.minimumTotal', {
          type: 'custom',
          message: 'Minimum total cannot be greater than maximum total.',
        });
        return;
      }
    }

    const prices = getPricesFromProductObject(products);
    try {
      setIsSaving(true);
      onSavingChange?.(true);

      if (isInPlaceEdit) {
        const rateEdit: QuoteOfferingRateEditReq = {
          name: rest.name,
          prices,
          internalDescription: rest.internalDescription ?? null,
          percentOfTotalConfig:
            offeringType === OfferingTypesEnum.CUSTOM_PERCENT_OF_TOTAL
              ? rest.percentOfTotalConfig
              : undefined,
        };
        await editRate.mutateAsync({
          quoteId: quoteOffering.quoteId,
          quoteOfferingId: quoteOffering.id,
          rate: rateEdit,
        });
        reset({ products, ...rest });
        const updatedRate = applyRateEditToRate(offeringRate, rateEdit);
        cacheOfferingRate(queryClient, updatedRate);
        setOfferingRate?.(updatedRate);
        setOrderedPricesUnderProduct?.(
          getOrderedPricesUnderProduct(updatedRate),
        );
        setIsEditMode(false);
        await onRateEdited?.(updatedRate);
      } else {
        const data = {
          ...rest,
          prices,
        };
        const savedRate = await createOfferingRate(
          data,
          offeringRate?.offering.id,
        );

        if (savedRate) {
          reset(data);
          queryClient.setQueryData<QuoteOfferingRateEditability>(
            [
              ...quoteOfferingRateServiceQueryKeys.editability(
                quoteOffering.quoteId,
                quoteOffering.id,
                savedRate.id,
              ),
            ],
            {
              rateId: savedRate.id,
              accountSpecificRate: true,
              editable: true,
              usedByOtherOfferingsOnThisQuote: false,
            },
          );
          setOfferingRate?.(savedRate);
          setOrderedPricesUnderProduct?.(
            getOrderedPricesUnderProduct(savedRate),
          );
          setIsEditMode(false);
          await onRateSaved?.(savedRate);
          // The quote now points at the clone, so the server can confirm the
          // seeded value. Not awaited — the drawer is already usable.
          void queryClient.invalidateQueries({
            queryKey: [
              ...quoteOfferingRateServiceQueryKeys.editability(
                quoteOffering.quoteId,
                quoteOffering.id,
                savedRate.id,
              ),
            ],
          });
        }
      }
    } catch (ex) {
      handleApiErrorToast(ex);
    } finally {
      setIsSaving(false);
      onSavingChange?.(false);
    }
  }

  function onError(error: any) {
    logger.error('[RATE FORM NAME ERROR]', error, methods);
  }

  function handleBulkPriceChange(data: BulkPriceChange[]) {
    // Reset form to original state
    reset(prepareRateRequestData(account, productsWithPrices, offeringRate));
    // Get new default values
    const currentValues = getValues();
    // Apply price change
    data.forEach(({ productId, percentage }) => {
      const product = currentValues.products[productId];
      if (product && product.prices) {
        const newPrices = product.prices.map((price) => {
          const newAmount =
            price.amount != null && !!percentage
              ? Math.max(
                  0,
                  roundNumberToDecimal(
                    Number(price.amount * (1 + percentage)),
                    10,
                  ),
                )
              : price.amount;
          return {
            ...price,
            amount: newAmount,
          };
        });
        setValue(`products.${productId}.prices`, newPrices, {
          shouldDirty: true,
        });
      }
    });
  }

  return (
    <form id="rate-form" onSubmit={handleSubmit(onSubmit, onError)}>
      <FormProvider {...methods} data-testid="rate-form">
        <MCard variant="borderless" mb="2">
          <Card.Body p={0}>
            <PricesDrawerRateHeader
              offeringRate={offeringRate}
              isEditMode={isEditMode}
              disabled={disabled}
              editability={editability}
              isEditabilityLoading={isEditabilityLoading}
              onClone={handleClone}
              onEdit={handleEdit}
              onCancelEdit={handleCancelEdit}
              showPriceTierLock={eligibleProductIds.size > 0}
              isPriceTierLockingMode={isPriceTierLockingMode}
              onManagePriceTierLocking={() => setIsPriceTierLockingMode(true)}
            />

            {isPriceTierLockingMode ? (
              <PricesDrawerPriceTierLocking
                account={account}
                offeringRate={offeringRate}
                productsWithPrices={selectedProductsWithPrices}
                allProductsWithPrices={productsWithPrices}
                productsObj={productsObj}
                currency={offeringRate.currency}
                eligibleProductIds={eligibleProductIds}
                quoteOffering={quoteOffering}
                onRateSaved={onRateSaved}
                onSavingChange={onSavingChange}
                onClose={() => setIsPriceTierLockingMode(false)}
              />
            ) : (
              <>
                {/* Bulk price change via percentage */}
                {accountSpecificRateAllowPercentageAdjustments && (
                  <>
                    {isEditMode && (
                      <>
                        <MDivider my="2" />
                        <ExpandCollapseButton
                          isExpanded={isBulkPriceChangeActive}
                          collapsedLabel="Percentage Price Adjustment"
                          expandedLabel="Percentage Price Adjustment"
                          onChange={() =>
                            setIsBulkPriceChangeActive((prev) => !prev)
                          }
                        />
                      </>
                    )}

                    {isBulkPriceChangeActive && (
                      <PricesDrawerBulkEditPrices
                        selectedProductsWithPrices={selectedProductsWithPrices}
                        onSubmit={handleBulkPriceChange}
                      />
                    )}
                  </>
                )}

                <MDivider my="2" />

                <MFlex justifyContent="end" align="center">
                  <ExpandCollapseButton
                    isExpanded={shouldExpandAllProducts}
                    onChange={(allExpanded: boolean) => {
                      setShouldExpandAllProducts(allExpanded);
                    }}
                  />
                </MFlex>

                {sortedProductsWithPrices.map((productWithPrices, index) => {
                  const isLastRow =
                    selectedProductsWithPrices.length - 1 === index;

                  return (
                    <Fragment key={productWithPrices.id}>
                      <PricesDrawerBodyEditProduct
                        offeringType={offeringType}
                        productWithPrices={productWithPrices}
                        productsObj={productsObj}
                        offeringRate={offeringRate}
                        isEditMode={isEditMode}
                        shouldExpandAllProducts={shouldExpandAllProducts}
                        pricingDisplayMaxThreshold={PRICES_COLLAPSE_THRESHOLD}
                      />
                      {!isLastRow && <MDivider mb="2" />}
                    </Fragment>
                  );
                })}
                {offeringRate.percentOfTotalConfig && (
                  <PricesDrawerScopedProducts
                    {...offeringRate.percentOfTotalConfig}
                  />
                )}
                {isEditMode && (
                  <MFlex
                    justifyContent="center"
                    align="center"
                    position="sticky"
                    bottom="0"
                    background="tWhite.base"
                    padding="12px"
                    borderTop="1px solid #e2e8f0"
                    zIndex="10"
                  >
                    <MButton
                      onClick={() => onCancel()}
                      variant="cancel"
                      isDisabled={isSaving}
                      mr={2}
                    >
                      Cancel
                    </MButton>
                    <MButton
                      variant="primary"
                      type="submit"
                      isLoading={isSaving}
                      isDisabled={!isDirty || isSaving}
                      onClick={handleSubmit(onSubmit, onError)}
                    >
                      Save
                    </MButton>
                  </MFlex>
                )}
              </>
            )}
          </Card.Body>
        </MCard>
      </FormProvider>
    </form>
  );
};
